import { ButtonBase, DialogTitle, Divider, IconButton } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import clsx from "clsx";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { meterToMiles } from "../../assets/helperFunctions";
import { useStyles as useDialogStyles } from "../../assets/jss/Dialog";
import { useStyles } from '../../assets/jss/DriverDetailsTabMileageClaim';
import { RecordStatus, TripTypeEnum } from "../../config/app-globals.enum";
import { CreateMileageClaimAPI, GetDriverTripListForMileageClaim, GetMileageClaimTripDistanceAPI } from "../../services/MileageClaimAPIService";
import CustomBox from "../CustomComponents/CustomBox/CustomBox";
import CustomButton from "../CustomComponents/CustomButton/CustomButton";
import CustomCircularProgress from "../CustomComponents/CustomCircularProgress/CustomCircularProgress";
import CustomDialog from "../CustomComponents/CustomDialog/CustomDialog";
import CustomDialogAction from "../CustomComponents/CustomDialogAction/CustomDialogAction";
import CustomDialogContent from "../CustomComponents/CustomDialogContent/CustomDialogContent";
import CustomGrid from "../CustomComponents/CustomGrid/CustomGrid";
import CustomTextField from "../CustomComponents/CustomTextField/CustomTextField";
import DateTime from "../DateTime/DateTime";
import SwitchTripDialog from "../Dialogs/SwitchTrip/SwitchTrip";
import ErrorAlert from "../ErrorAlert/ErrorAlert";

const DialogContent = withStyles((theme) => ({}))(CustomDialogContent);
const DialogActions = withStyles((theme) => ({}))(CustomDialogAction);

export default function DriverDetailsTabMileageClaimDialog(props) {

    const { t } = useTranslation();
    const dialogClasses = useDialogStyles();
    const classes = useStyles();

    const [mileageClaimDetailsData, setMileageClaimDetailsData] = useState(null);
    const [isDataChanged, setIsDataChanged] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [fromDate, setFromDate] = useState<Date>();
    const [toDate, setToDate] = useState<Date>();

    const handleClose = (shouldRefresh) => {
        props.handleCloseShowDriverDetailsTabMileageClaimDialog(shouldRefresh);
    }

    const handleShowTrips = (val: boolean) => {
        if (val) {
            getUserTripList();
        }
        setShowTrips(val);
    }

    const getUserTripList = async () => {
        if (isTripListLoading) {
            return;
        }
        setIsTripListLoading(true);
        var parameters = {
            userId: props.userId,
            fromDate: fromDate,
            toDate: toDate,
            tripTypes: showMissingTrips ? [TripTypeEnum.PERSONAL, TripTypeEnum.NOT_DRIVING, TripTypeEnum.OTHER] : [TripTypeEnum.BUSINESS]
        };
        const data = await GetDriverTripListForMileageClaim(parameters);
        if (
            data &&
            (data.status.code === 0 ||
                data.status.code === 200 ||
                data.status.code === 201) &&
            data.content) {
            if (data.content.length > 0) {
                data.content.sort((a, b) => new Date(b.endTime).getTime() - new Date(a.endTime).getTime());
                for (let i = 0; i < data.content.length; i++) {
                    const trip = data.content[i];
                    const startAddress = getAddressFromObject(trip.startAddress);
                    const endAddress = getAddressFromObject(trip.endAddress);
                    trip['formattedStartAddress'] = startAddress;
                    trip['formattedEndAddress'] = endAddress;
                }
            }
            setTripList(data.content);
        } else {
            setStatusValue(data.status.code);
            setAlertMessage(t("ERROR_WHILE_GETTING_TRIP_LIST"));
            setAlertType("error");
            setIsAlertOpen(true);
        }
        setIsTripListLoading(false);
    }

    const [tripList, setTripList] = useState([]);
    const [isTripListLoading, setIsTripListLoading] = useState(false);
    const [showTrips, setShowTrips] = useState(false);
    const [showMissingTrips, setShowMissingTrips] = useState(false);

    useEffect(() => {
        getUserTripList();
    }, [showMissingTrips])

    useEffect(() => {
        if (!showTrips) {
            setShowMissingTrips(false);
        }
    }, [showTrips])

    const getTripDistance = async (fromDate: Date, toDate: Date) => {
        setIsLoading(true);
        const data = await GetMileageClaimTripDistanceAPI(fromDate, toDate);
        if (data &&
            (data.status.code === 0 ||
                data.status.code === 200 ||
                data.status.code === 201) &&
            data.content) {
            setMileageClaimDetailsData(data.content);
            setFromDate(new Date(data.content.fromDate));
            setToDate(new Date(data.content.toDate));
            setIsDataChanged(false);
        } else {
            setStatusValue(data.status.code);
            setAlertMessage(t("ERROR_WHILE_GETTING_TRIPS_DISTANCE"));
            setAlertType("error");
            setIsAlertOpen(true);
        }
        setIsLoading(false);
    }

    const [maxDate, setMaxDate] = useState(new Date());
    useEffect(() => {
        getTripDistance(null, null);
        setMaxDate(new Date());
    }, [])

    const handleClickStartDatePicker = (event) => {
        if (event?.target?.value) {
            let timezoneOffset = (new Date()).getTimezoneOffset();
            let localDate = new Date(event.target.value);
            localDate.setMinutes(localDate.getMinutes() + timezoneOffset);
            setFromDate(localDate);
            setMileageClaimDetailsData({ ...mileageClaimDetailsData, fromDate: localDate.toISOString() });
            setIsDataChanged(true);
            setShowTrips(false);
        }
    };

    const handleClickEndDatePicker = (event) => {
        if (event?.target?.value) {
            let timezoneOffset = (new Date()).getTimezoneOffset();
            let localDate = new Date(event.target.value);
            localDate.setMinutes(localDate.getMinutes() + timezoneOffset);
            setToDate(localDate);
            setMileageClaimDetailsData({ ...mileageClaimDetailsData, toDate: localDate.toISOString() });
            setIsDataChanged(true);
            setShowTrips(false);
        }
    };

    const formatDate = (date: Date) => {
        let formatedDate = "";
        try {
            formatedDate = moment(date).format('YYYY-MM-DD');
        } catch (error) {
        }
        return formatedDate;
    }

    const getAddressFromObject = (addressObject) => {
        let address = '';
        try {
            if (addressObject) {
                if (addressObject.subLocality !== undefined && addressObject.subLocality !== null) {
                    address += addressObject.subLocality + ', ';
                }
                if (addressObject.locality !== undefined && addressObject.locality !== null) {
                    address += addressObject.locality + ', ';
                }
                if (addressObject.subAdminArea !== undefined && addressObject.subAdminArea !== null) {
                    address += addressObject.subAdminArea + ', ';
                }
                if (addressObject.adminArea !== undefined && addressObject.adminArea !== null) {
                    address += addressObject.adminArea + ', ';
                }
                if (addressObject.countryName !== undefined && addressObject.countryName !== null) {
                    address += addressObject.countryName + ', ';
                }
                if (addressObject.postalCode !== undefined && addressObject.postalCode !== null) {
                    address += addressObject.postalCode;
                }
            }
        } catch (error) {

        }
        return address === '' ? t(`LOCATION_DATA_NOT_FOUND`) : address; //address.replace(/, +$/, '');
    }

    const getTripType = (tripType: number) => {
        if (TripTypeEnum.BUSINESS === tripType) {
            return <>{t(`BUSINESS`)}</>
        } else if (TripTypeEnum.NOT_DRIVING === tripType) {
            return <>{t(`I_WAS_NOT_DRIVING`)}</>
        } else if (TripTypeEnum.OTHER === tripType) {
            return <>{t(`OTHER`)}</>
        } else if (TripTypeEnum.PERSONAL === tripType) {
            return <>{t(`PERSONAL_TRIP`)}</>
        }
    }

    const getTotalCost = (mileageClaimDetailsData: any) => {
        try {
            if (mileageClaimDetailsData && mileageClaimDetailsData.totalDistanceInMeter && mileageClaimDetailsData.pricePerMile) {
                return +(meterToMiles(mileageClaimDetailsData.totalDistanceInMeter, false)) * mileageClaimDetailsData.pricePerMile
            }
        } catch (error) {
            console.warn(error);
        }
        return 0;
    }

    const checkMileageButtonClicked = () => {
        getTripDistance(fromDate, toDate);
    }

    const [selectedTrip, setSelectedTrip] = useState(null);
    const [isSwitchTripDialogOpened, setIsSwitchTripDialogOpened] = useState(false);
    const reclassifyTripButtonClicked = (trip: any, openDialog: boolean) => {
        setIsSwitchTripDialogOpened(openDialog);
        setSelectedTrip(trip);
    }

    const setIsFreshCall = (shouldRefresh: boolean) => {
        if (shouldRefresh) {
            setIsDataChanged(true);
            getUserTripList();
        }
    }

    const claimButtonClicked = async () => {
        if (isLoading) {
            return;
        }
        if (mileageClaimDetailsData.totalDistanceInMeter === 0) {
            setAlertMessage(t("TOTAL_DISTANCE_CANNOT_BE_ZERO"));
            setAlertType("error");
            setIsAlertOpen(true);
            return;
        }
        setIsLoading(true);
        let parameters = {
            userId: props.userId,
            distanceInMeter: mileageClaimDetailsData.totalDistanceInMeter,
            pricePerMile: mileageClaimDetailsData.pricePerMile,
            totalCost: getTotalCost(mileageClaimDetailsData),
            status: RecordStatus.ACTIVE,
            claimFrom: fromDate,
            claimTo: toDate,
        }
        const data = await CreateMileageClaimAPI(parameters);
        if (data &&
            (data.status.code === 0 ||
                data.status.code === 200 ||
                data.status.code === 201)) {
            setStatusValue(data.status.code);
            setAlertMessage(t("CREATED_MILEAGE_CLEAIM_SUCCESSFLLY"));
            setAlertType("success");
            setIsAlertOpen(true);
            setTimeout(() => {
                handleClose(true);
            }, 2500);
        } else {
            setAlertType("error");
            setStatusValue(data.status.code);
            setAlertMessage(t("ERROR_WHILE_CREATING_MILEAGE_CLEAIM"));
            setIsAlertOpen(true);
            setIsLoading(false);
        }
    }

    const [isAlertOpen, setIsAlertOpen] = useState(false);
    const [statusValue, setStatusValue] = useState(0);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertType, setAlertType] = useState("error");

    return (
        <>
            <CustomDialog
                open={true}
                className={clsx(dialogClasses.dialogWrapper, classes.dialogWrapper)}>
                <DialogTitle id="customized-dialog-title" className={classes.dialogTitle}>
                    <CustomBox className={classes.headerTitle}>
                        {t('CLAIM_MILEAGE_EXPENSES')}
                    </CustomBox>
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    className={dialogClasses.closeButton}
                    onClick={() => handleClose(false)}>
                    <CloseIcon />
                </IconButton>
                <DialogContent className={clsx(dialogClasses.dialogContentWrapper, classes.dialogContentWrapper)}>
                    <CustomGrid container>
                        {/* {!showTrips && */}
                        <CustomBox className={classes.mileageClaimBox}>
                            {isLoading && <CustomBox>
                                <h3 className={clsx(classes.headerTitle, classes.loaderHeader)}>
                                    <CustomCircularProgress />
                                    <CustomBox paddingLeft={1}>
                                        {t('Loading')}
                                    </CustomBox>
                                </h3>
                            </CustomBox>}
                            {!isLoading &&
                                <CustomBox>
                                    <CustomBox>
                                        <CustomGrid container spacing={4} justifyContent="space-between">
                                            <CustomGrid item xs={6}>
                                                <CustomBox className={classes.boxHeader}>
                                                    {t('DATE')}
                                                </CustomBox>

                                                <CustomTextField
                                                    required
                                                    id="startDate"
                                                    disabled={false}
                                                    fullWidth
                                                    type="date"
                                                    format="DD-MM-YYYY"
                                                    value={formatDate(fromDate)}
                                                    maxdate={formatDate(maxDate)}
                                                    onChange={handleClickStartDatePicker}
                                                    InputProps={{
                                                        disableUnderline: true,
                                                    }}
                                                    InputLabelProps={{ shrink: false }}
                                                />
                                            </CustomGrid>
                                            <CustomGrid item xs={6}>
                                                <CustomBox>
                                                    &nbsp;
                                                </CustomBox>
                                                <CustomTextField
                                                    required
                                                    id="endDate"
                                                    disabled={false}
                                                    fullWidth
                                                    type="date"
                                                    format="DD-MM-YYYY"
                                                    value={formatDate(toDate)}
                                                    maxdate={formatDate(maxDate)}
                                                    onChange={handleClickEndDatePicker}
                                                    InputProps={{
                                                        disableUnderline: true,
                                                    }}
                                                    InputLabelProps={{ shrink: false }}
                                                />
                                            </CustomGrid>
                                        </CustomGrid>
                                    </CustomBox>
                                    {(!isDataChanged && mileageClaimDetailsData) ?
                                        <>
                                            <CustomBox>
                                                <CustomGrid container spacing={4} justifyContent="space-between">
                                                    <CustomGrid item xs={6}>
                                                        <CustomBox className={classes.boxHeader}>
                                                            {t('DISTANCE')}
                                                        </CustomBox>
                                                        <CustomBox className={classes.boxOuter}>
                                                            {meterToMiles(mileageClaimDetailsData.totalDistanceInMeter, false)}
                                                        </CustomBox>
                                                    </CustomGrid>
                                                    <CustomGrid item xs={6}>
                                                        <CustomBox className={classes.boxHeader}>
                                                            {t('DISTANCE_UNIT')}
                                                        </CustomBox>
                                                        <CustomBox className={classes.boxOuter}>
                                                            {t('MILES')}
                                                        </CustomBox>
                                                    </CustomGrid>
                                                </CustomGrid>
                                            </CustomBox>
                                            <CustomBox>
                                                <CustomGrid container spacing={4} justifyContent="space-between">
                                                    <CustomGrid item xs={12}>
                                                        <CustomBox className={classes.boxHeader}>
                                                            {t('PRICE_PER_MILE')}
                                                        </CustomBox>
                                                        <CustomBox className={classes.boxOuter}>
                                                            £ {mileageClaimDetailsData.pricePerMile}
                                                        </CustomBox>
                                                    </CustomGrid>
                                                </CustomGrid>
                                            </CustomBox>
                                            <CustomBox>
                                                <CustomGrid container spacing={4} justifyContent="space-between">
                                                    <CustomGrid item xs={12}>
                                                        <CustomBox className={classes.boxHeader}>
                                                            {t('TOTAL_COST')}
                                                        </CustomBox>
                                                        <CustomBox className={classes.boxOuter}>
                                                            £ {getTotalCost(mileageClaimDetailsData)}
                                                        </CustomBox>
                                                    </CustomGrid>
                                                </CustomGrid>
                                            </CustomBox>
                                            <CustomBox className={classes.buttonOuterBox}>
                                                {
                                                    <>
                                                        {!showTrips &&
                                                            <CustomButton
                                                                className={classes.noBackground}
                                                                onClick={() => handleShowTrips(true)}
                                                                color="primary"
                                                                defaultrounded="true">
                                                                {t("SHOW_TRIPS")}
                                                            </CustomButton>}
                                                        <CustomButton
                                                            color="primary"
                                                            defaultrounded="true"
                                                            onClick={() => claimButtonClicked()}
                                                            variant="contained">
                                                            {t("CLAIM")}
                                                        </CustomButton>
                                                    </>
                                                }
                                            </CustomBox>
                                        </>
                                        :
                                        <CustomBox padding={1} textAlign="center">
                                            <CustomButton
                                                onClick={() => checkMileageButtonClicked()}
                                                color="primary"
                                                defaultrounded="true"
                                                variant="contained">
                                                {t("CHECK_MILEAGE")}
                                            </CustomButton>
                                        </CustomBox>
                                    }
                                </CustomBox>
                            }
                        </CustomBox>
                        {/* } */}
                        {showTrips && <>
                            <CustomBox className={classes.mainDivider}></CustomBox>
                            {/* {showTrips && */}
                            <CustomBox className={classes.showTripsBox}>
                                {isTripListLoading && <CustomBox>
                                    <h3 className={clsx(classes.headerTitle, classes.loaderHeader)}>
                                        <CustomCircularProgress />
                                        <CustomBox paddingLeft={1}>
                                            {t('Loading')}
                                        </CustomBox>
                                    </h3>
                                </CustomBox>}
                                {mileageClaimDetailsData && !isTripListLoading &&
                                    <>
                                        <CustomBox paddingBottom={1} textAlign="center">
                                            {showMissingTrips ?
                                                <CustomBox className={classes.headerTitle}>
                                                    {t('TRIPS_NOT_INCLUDED_IN_CLAIM')}
                                                </CustomBox>
                                                :
                                                <CustomBox className={classes.headerTitle}>
                                                    {t('TRIPS_INCLUDED_IN_CLAIM')}
                                                </CustomBox>
                                            }
                                        </CustomBox>
                                        <CustomBox className={classes.horizontalDivider}></CustomBox>
                                        <CustomBox style={{ height: '300px', overflow: 'auto' }}>
                                            <CustomBox>
                                                {tripList && (tripList.length > 0) && tripList.map((trip, index) => {
                                                    return <ButtonBase key={index} component="div" style={{ display: 'block' }}>
                                                        <CustomBox className={clsx(classes.tripsListItem)}>
                                                            <CustomBox className={classes.tripsListItemRow}>
                                                                <CustomBox className={classes.tripsListItemIconBox}>
                                                                    <CustomBox className={clsx(classes.tripsListItemIconStart)}>
                                                                    </CustomBox>
                                                                    <CustomBox className={classes.dottedLine}></CustomBox>
                                                                </CustomBox>
                                                                <CustomBox className={classes.tripsListItemSideBox}>
                                                                    <CustomBox className={trip.formattedStartAddress === t(`LOCATION_DATA_NOT_FOUND`) ? classes.eventNoAddress : classes.eventAddress}>
                                                                        {trip.formattedStartAddress}
                                                                    </CustomBox>
                                                                    <CustomBox className={classes.eventDate}>
                                                                        <DateTime inputDate={trip.startTime} returnFormat="DD MMM YYYY" /> |{" "}<DateTime inputDate={trip.startTime} returnFormat="hh:mm A" />
                                                                    </CustomBox>
                                                                </CustomBox>
                                                            </CustomBox>
                                                            <CustomBox className={classes.tripsListItemRow}>
                                                                <CustomBox className={classes.tripsListItemIconBox}>
                                                                    <CustomBox className={clsx(classes.tripsListItemIconEnd)}>

                                                                    </CustomBox>
                                                                </CustomBox>
                                                                <CustomBox className={classes.tripsListItemSideBox}>
                                                                    <CustomBox className={trip.formattedEndAddress === t(`LOCATION_DATA_NOT_FOUND`) ? classes.eventNoAddress : classes.eventAddress}>
                                                                        {trip.formattedEndAddress}
                                                                    </CustomBox>
                                                                    <CustomBox className={classes.eventDate}>
                                                                        <DateTime inputDate={trip.endTime} returnFormat="DD MMM YYYY" /> |{" "}<DateTime inputDate={trip.endTime} returnFormat="hh:mm A" />
                                                                    </CustomBox>
                                                                </CustomBox>
                                                            </CustomBox>
                                                            <CustomBox className={clsx(classes.tripsListItemRow, classes.tripsListItemRowCenter)}>
                                                                <CustomGrid container alignItems="center" justifyContent="space-between">
                                                                    <CustomGrid item className={classes.eventsSeverityCount}>
                                                                        {t('MILEAGE')}: {+(trip.distance.toFixed(2))} {t('MILES')}
                                                                    </CustomGrid>
                                                                    <CustomGrid item className={classes.eventsSeverityCount}>
                                                                        {t('TOTAL_COST')}: £ {+((trip.distance * mileageClaimDetailsData.pricePerMile).toFixed(2))}
                                                                    </CustomGrid>
                                                                </CustomGrid>
                                                            </CustomBox>
                                                            <CustomBox className={classes.reclassifyTrips}>
                                                                <CustomBox className={classes.eventsType}>
                                                                    {getTripType(trip.tripType)}
                                                                    <CustomBox className={classes.eventsTypeButton} onClick={() => reclassifyTripButtonClicked(trip, true)}>
                                                                        {t(`RECLASSIFY_TRIP`)}
                                                                    </CustomBox>
                                                                </CustomBox>
                                                            </CustomBox>
                                                        </CustomBox>
                                                        <Divider light />
                                                    </ButtonBase>
                                                })}
                                                {tripList && (tripList.length === 0) &&
                                                    <CustomBox>
                                                        {t('NO_RESULT_FOUND')}
                                                    </CustomBox>
                                                }
                                            </CustomBox>
                                        </CustomBox>
                                    </>}
                            </CustomBox>
                        </>}
                        {/* } */}
                    </CustomGrid >
                </DialogContent>

                {isSwitchTripDialogOpened &&
                    <SwitchTripDialog
                        selectedTrip={selectedTrip}
                        setIsFreshCall={setIsFreshCall}
                        setSelectedTrip={setSelectedTrip}
                        userId={props.userId}
                        dialogOpen={isSwitchTripDialogOpened}
                        dialogClose={() => reclassifyTripButtonClicked(null, false)}
                    />
                }

                <DialogActions className={classes.dialogActionWrapper}>
                    {(!isLoading && !isTripListLoading) && <>
                        {/* {!showTrips &&
                            <CustomButton
                                className={classes.noBackground}
                                onClick={() => handleShowTrips(true)}
                                color="primary"
                                defaultrounded="true">
                                {t("SHOW_TRIPS")}
                            </CustomButton>} */}
                        {showTrips &&
                            <>
                                {
                                    showMissingTrips ?
                                        <CustomButton
                                            className={classes.noBackground}
                                            onClick={() => setShowMissingTrips(false)}
                                            // color="primary"
                                            // variant="contained"
                                            color="primary"
                                            defaultrounded="true">
                                            {t("SHOW_BUSSINESS_TRIPS")}
                                        </CustomButton> :
                                        <CustomButton
                                            className={classes.noBackground}
                                            onClick={() => setShowMissingTrips(true)}
                                            // color="primary"
                                            // variant="contained"
                                            color="primary"
                                            defaultrounded="true">
                                            {t("SHOW_MISSING_TRIPS")}
                                        </CustomButton>
                                }
                                {/* <CustomButton
                                    className={classes.noBackground}
                                    onClick={() => handleShowTrips(false)}
                                    color="primary"
                                    defaultrounded="true">
                                    {t("BACK")}
                                </CustomButton> */}
                            </>
                        }
                    </>}
                    <CustomButton
                        autoFocus
                        onClick={() => handleClose(false)}
                        color="primary"
                        className={dialogClasses.noBg}>
                        {t("CLOSE")}
                    </CustomButton>

                </DialogActions>
            </CustomDialog>

            <ErrorAlert
                code={statusValue}
                message={alertMessage}
                isOpen={isAlertOpen}
                alertType={alertType}
                isClose={(isClose) => setIsAlertOpen(!isClose)}
            />
        </>
    )
}